import { useMutation, useQueryClient } from 'react-query';

import useApiHelper from '../../hooks/useApiHelper';
import { useDefaultMutateResponseHandlers } from '../../hooks/useDefaultMutateResponeHandlers';
import { applianceQueryKeys, planetDeviceQueryKeys } from '../QueryKeys';
import {
  IAppliance,
  IApplianceCreateRequest,
  IApplianceUpdateRequest,
} from '../../types/Appliance/Appliance';
import { IDefaultResponseWithData } from '../../types/system/DefaultResponse';

export default function useApplianceMutations() {
  const queryClient = useQueryClient();
  const { post, put, del } = useApiHelper();
  const { handleSuccess, handleError } =
    useDefaultMutateResponseHandlers('Appliance');

  const createAppliance = useMutation({
    mutationFn: (appliance: IApplianceCreateRequest) =>
      post<IApplianceCreateRequest, IDefaultResponseWithData<IAppliance>>(
        '/v1/appliance',
        appliance
      ),
    onError: (error) => handleError(error, 'create'),
    onSuccess: (data) => handleSuccess(data, 'create'),
    onSettled: () => {
      queryClient.invalidateQueries(applianceQueryKeys.all());
      queryClient.invalidateQueries(planetDeviceQueryKeys.all());
    },
  });

  const updateAppliance = useMutation({
    mutationFn: (appliance: IApplianceUpdateRequest) =>
      put<IApplianceUpdateRequest, IDefaultResponseWithData<IAppliance>>(
        `/v1/appliance/${appliance.id}`,
        appliance
      ),
    onError: (error) => handleError(error, 'update'),
    onSuccess: (data) => handleSuccess(data, 'update'),
    onSettled: () => {
      queryClient.invalidateQueries(applianceQueryKeys.all());
      queryClient.invalidateQueries(planetDeviceQueryKeys.all());
    },
  });

  const delAppliance = useMutation({
    mutationFn: (appliance: IAppliance) => del(`/v1/appliance/${appliance.id}`),
    onError: (error) => handleError(error, 'delete'),
    onSuccess: (data) => handleSuccess(data, 'delete'),
    onSettled: () => {
      queryClient.invalidateQueries(applianceQueryKeys.all());
      queryClient.invalidateQueries(planetDeviceQueryKeys.all());
    },
  });

  return {
    createAppliance,
    updateAppliance,
    delAppliance,
  };
}
